import type {
  Ad as PlayerAd,
  AdBreak as PlayerAdBreak,
  AdBreakConfig as PlayerAdBreakConfig,
  AdTagConfig as PlayerAdTagConfig,
  ErrorEvent as PlayerErrorEvent,
  PlayerEventBase,
  LinearAd as PlayerLinearAd,
  VastAdData as PlayerVastAdData,
} from 'bitmovin-player';

import type {AdBreakInfo} from '../../../types/ads/AdBreakInfo';
import type {AdInfo} from '../../../types/ads/AdInfo';
import {isNotNullish, isNullish} from '../../../utils/Utils';
import type {AdErrorPayload} from '../../internal/ads/AdModuleAPI';

export function mapBitmovinAdError(event: PlayerEventBase, activeAdId?: string): AdErrorPayload {
  const adErrorPayload: AdErrorPayload = {
    activeAdId,
  };

  const errorEvent = event as PlayerErrorEvent;
  const errorData = errorEvent.data;
  if (isNotNullish(errorData)) {
    adErrorPayload.code = errorData.code || errorEvent.code;
    adErrorPayload.message = errorData.message || errorEvent.name;

    if ('adBreak' in errorData || 'adConfig' in errorData) {
      const adInfo = errorData.adBreak ?? errorData.adConfig;
      adErrorPayload.adBreakInfo = mapBitmovinAdBreak(adInfo);
    }
  }

  return adErrorPayload;
}

export function mapBitmovinAd(ad: PlayerAd): AdInfo {
  const adInfo: AdInfo = {
    id: ad.id,
    isLinear: ad.isLinear,
    clickThroughUrl: ad.clickThroughUrl,
    width: ad.width,
    height: ad.height,
  };

  const linearAd = ad.isLinear ? (ad as PlayerLinearAd) : undefined;
  if (isNotNullish(linearAd)) {
    adInfo.durationInSec = linearAd.duration;
    adInfo.skippable = isNotNullish(linearAd.skippableAfter);
    adInfo.skippableAfter = linearAd.skippableAfter;
    adInfo.mediaUrl = linearAd.mediaFileUrl;
  }

  const vastData = ad.data as PlayerVastAdData | undefined;
  if (isNotNullish(vastData)) {
    adInfo.bitrate = vastData.bitrate;
    adInfo.mimeType = vastData.mimeType;
    adInfo.vastData = {
      adSystem: vastData.adSystem?.name,
      advertiserName: vastData.advertiser?.name,
      apiFramework: vastData.apiFramework,
      creativeAdId: vastData.creative?.adId,
      creativeId: vastData.creative?.id,
      universalAdIdRegistry: vastData.creative?.universalAdId?.idRegistry,
      universalAdIdValue: vastData.creative?.universalAdId?.value,
      adDescription: vastData.adDescription,
      adTitle: vastData.adTitle,
      wrapperAdsCount: vastData.wrapperAdIds?.length ?? 0,
      surveyUrl: vastData.survey?.uri,
      minSuggestedDuration: vastData.minSuggestedDuration,
    };
  }

  return adInfo;
}

export function mapBitmovinAdBreak(adConfig: PlayerAdBreak | PlayerAdTagConfig): AdBreakInfo {
  const adBreakInfo: AdBreakInfo = {
    replaceContentDuration: adConfig.replaceContentDuration,
    fallbackIndex: getFallbackIndex(adConfig),
  };

  // AdBreak
  adBreakInfo.id = 'id' in adConfig ? adConfig.id : undefined;
  adBreakInfo.scheduleTime = 'scheduleTime' in adConfig ? adConfig.scheduleTime : undefined;

  // AdTagConfig / AdBreakConfig
  const adTagConfig = adConfig as PlayerAdTagConfig;
  const {tagUrl, tagType} = resolveEffectiveAdTag(adTagConfig);
  adBreakInfo.tagUrl = tagUrl;
  adBreakInfo.tagType = tagType;
  adBreakInfo.persistent = 'persistent' in adConfig ? adTagConfig.persistent : undefined;
  adBreakInfo.position = 'position' in adConfig ? adConfig.position : undefined;
  adBreakInfo.preloadOffset = 'preloadOffset' in adConfig ? (adConfig as PlayerAdBreakConfig).preloadOffset : undefined;

  return adBreakInfo;
}

/**
 * Resolves the effective ad tag URL and type, applying the fallback tag
 * if `fallbackIndex` indicates a fallback is active.
 */
export function resolveEffectiveAdTag(adTagConfig: PlayerAdTagConfig): {tagType?: string; tagUrl?: string} {
  if (isNullish(adTagConfig.tag)) {
    return {};
  }

  const fallbackIndex = getFallbackIndex(adTagConfig);
  const directTag = {tagType: adTagConfig.tag.type, tagUrl: adTagConfig.tag.url};
  if (isNullish(adTagConfig.fallbackTags) || fallbackIndex <= 0 || adTagConfig.fallbackTags.length < fallbackIndex) {
    return directTag;
  }

  const fallbackTag = adTagConfig.fallbackTags[fallbackIndex - 1];
  return {tagType: fallbackTag.type, tagUrl: fallbackTag.url};
}

/**
 * Maps the Bitmovin player's internal `currentFallbackIndex` to the analytics `adFallbackIndex`.
 *
 * Player convention (ImaAdBreak.currentFallbackIndex):
 *   - absent/null → no fallback tracking (treat as primary ad)
 *   - `0`         → primary ad is playing
 *   - `1`         → first fallback tag (`fallbackTags[0]`) is playing
 *   - `2`         → second fallback tag (`fallbackTags[1]`) is playing
 *
 * Analytics convention matches 1:1, so the mapping is an identity for defined values.
 * Both absent and `0` map to `adFallbackIndex = 0` (primary ad, no fallback used).
 */
export function getFallbackIndex(adConfig: PlayerAdBreak | PlayerAdTagConfig): number {
  const rawFallbackIndex = (adConfig as any).currentFallbackIndex;

  if (isNullish(rawFallbackIndex)) {
    return 0;
  }

  return rawFallbackIndex;
}
